/**
 * Storage URL Proxy Utility
 *
 * When running against local Docker Supabase, storage URLs point to
 * http://127.0.0.1:55321 or http://localhost:55321 which are only
 * reachable from the host machine. When the app is accessed remotely
 * (e.g. via ngrok), these URLs break because the remote browser
 * cannot reach 127.0.0.1.
 *
 * This utility rewrites local storage URLs to use a proxied path
 * (/supabase-storage/...) that Next.js rewrites handle, keeping all
 * requests flowing through the same origin. See next.config.js rewrites().
 *
 * Production URLs (https://*.supabase.co) are returned unchanged.
 */

// 54321 is the old local port; files uploaded before the move still carry it.
const LOCAL_STORAGE_PATTERN = /^https?:\/\/(127\.0\.0\.1|localhost):(54321|55321)\/storage\//;

const NGROK_HOSTS = ['.ngrok-free.dev', '.ngrok.dev'];

/**
 * Converts local Supabase storage URLs to proxied relative paths.
 * Handles both direct local URLs (http://127.0.0.1:55321/storage/...)
 * and ngrok-tunneled URLs (https://xxx.ngrok-free.dev/storage/...).
 * Production URLs (*.supabase.co) are returned unchanged.
 *
 * The ngrok rule matches on the parsed HOST, not on a substring of the whole
 * URL. It used to be `url.includes('.ngrok.dev/storage/')`, which fired on any
 * URL merely CONTAINING that text — so a production
 * `*.supabase.co/...?ref=https://x.ngrok.dev/storage/y` was rewritten to a
 * relative /supabase-storage/ path that 404s outside dev. Matching the host
 * also makes this function's domain provably disjoint from
 * resizedStorageImageUrl's (a host cannot end with both `.supabase.co` and
 * `.ngrok.dev`), which is what lets the two compose in either order (#4019).
 */
export function proxyStorageUrl(url: string | null | undefined): string | null {
  if (!url) return null;

  // Rewrite local Docker Supabase URLs → /supabase-storage/ proxy
  if (LOCAL_STORAGE_PATTERN.test(url)) {
    return url.replace(LOCAL_STORAGE_PATTERN, '/supabase-storage/');
  }

  // Rewrite ngrok-tunneled Supabase storage URLs → /supabase-storage/ proxy
  // (ngrok tunnel URLs are also unreachable if the tunnel restarts with a new subdomain)
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  if (NGROK_HOSTS.some((host) => parsed.hostname.endsWith(host)) && parsed.pathname.startsWith('/storage/')) {
    return url.replace(/^https?:\/\/[^/]+\/storage\//, '/supabase-storage/');
  }

  return url;
}
